import { useEffect, useState } from "react";

/**
 * Hook that debounces a value by the specified delay.
 * Useful for delaying API calls until user stops typing/changing filters.
 *
 * @param value - The value to debounce
 * @param delay - Delay in milliseconds (default: 500)
 * @returns The debounced value
 *
 * @example
 * const [filters, setFilters] = useState({ search: "", status: [] });
 * const debouncedFilters = useDebounced(filters, 500);
 *
 * // Use debouncedFilters in your API query
 * useQuery({ queryKey: ['users', debouncedFilters], ... });
 */
export function useDebounced<T>(value: T, delay = 500): T {
	const [debouncedValue, setDebouncedValue] = useState<T>(value);

	useEffect(() => {
		const timeout = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);

		return () => clearTimeout(timeout);
	}, [value, delay]);

	return debouncedValue;
}
